/**
 * Production order time helpers:
 * - ProductionTimeCalculator (backend) / estimated_production_hours = throughput estimate.
 * - actualProductionDurationMinutes = realized wall time (end − start − pause).
 * - remainingProductionTime = ETA from estimate + pause extension (floor countdown).
 */

import type { ProductionOrderWithProduct } from '../services/productionOrderService';

export function actualProductionDurationMinutes(params: {
  actual_start_time: string | null | undefined;
  actual_end_time: string | null | undefined;
  total_pause_duration_seconds?: number | null;
}): number | null {
  const { actual_start_time, actual_end_time, total_pause_duration_seconds } = params;
  if (!actual_start_time || !actual_end_time) {
    return null;
  }
  const start = new Date(actual_start_time);
  const end = new Date(actual_end_time);
  const pauseMs = Math.max(0, (total_pause_duration_seconds ?? 0) * 1000);
  const rawMs = end.getTime() - start.getTime() - pauseMs;
  const clampedMs = Math.max(0, rawMs);
  return Math.round(clampedMs / 60000);
}

export type RemainingProductionTimeResult = {
  display: string;
  isOverdue: boolean;
  isPaused: boolean;
  remainingMs: number;
};

/** Pure countdown from estimate + total pause (seconds), matching prior dashboard behavior. */
export function remainingProductionTime(
  order: ProductionOrderWithProduct,
  now: Date
): RemainingProductionTimeResult {
  if (!order.actual_start_time || order.estimated_production_hours == null) {
    return { display: '', isOverdue: false, isPaused: false, remainingMs: 0 };
  }

  const startTime = new Date(order.actual_start_time);
  const estimatedMs = order.estimated_production_hours * 60 * 60 * 1000;
  const totalPauseMs =
    ((order as { total_pause_duration?: number }).total_pause_duration || 0) * 1000;

  const isPaused = Boolean((order as { is_paused?: boolean }).is_paused);
  if (isPaused) {
    return {
      display: '',
      isOverdue: false,
      isPaused: true,
      remainingMs: 0,
    };
  }

  const expectedEndTime = new Date(startTime.getTime() + estimatedMs + totalPauseMs);
  const remainingMs = expectedEndTime.getTime() - now.getTime();

  if (remainingMs <= 0) {
    return {
      display: 'OVERDUE',
      isOverdue: true,
      isPaused: false,
      remainingMs: 0,
    };
  }

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
  const timeDisplay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return {
    display: timeDisplay,
    isOverdue: false,
    isPaused: false,
    remainingMs,
  };
}

/** User-facing label; workspace rule: use minutes for time. */
export function formatActualProductionMinutesLabel(minutes: number | null): string {
  if (minutes === null) {
    return '';
  }
  return `${minutes} minutes`;
}
